import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import FactionBlockedState from '../components/faction/FactionBlockedState';
import EditFactionFormLineUp from '../components/faction/EditFactionFormLineUp';
import { mergeFactionWrestlersWithDetails } from '../utils/factionWrestlers';
import { usePremium } from '../premium/PremiumContext';
import { loadPremiumWrestlerInsights } from '../utils/premiumWrestlerInsights';

const getLatestWrestlerPrice = (data = {}) => {
  const prices = Array.isArray(data.prices) ? data.prices : [];
  const latestPrice = prices[prices.length - 1];
  const timelinePrice = Number(latestPrice?.value ?? latestPrice?.price ?? latestPrice);

  if (Number.isFinite(timelinePrice)) return timelinePrice;

  const directPrice = Number(data.price ?? data.cost ?? 0);
  return Number.isFinite(directPrice) ? directPrice : 0;
};

const normalizeWrestler = (snap, { includePremiumData = false } = {}) => {
  const data = snap.data();
  const {
    premiumComment,
    position,
    storyline,
    ...standardData
  } = data;
  const baseWrestler = {
    id: snap.id,
    ...standardData,
    name: data.name || data.nome || 'Sconosciuto',
    score: data.score ?? 0,
    scores: Array.isArray(data.scores)
      ? data.scores
      : Array.isArray(data.pastScores)
      ? data.pastScores
      : [],
    prices: Array.isArray(data.prices) ? data.prices : [],
    price: getLatestWrestlerPrice(data),
    imageUrl: data.imageUrl || '/assets/default-wrestler.png',
    brand: data.brand || '',
    gender: data.gender || null,
    alignment: data.alignment || '',
    active: data.active !== false,
    isInjured: data.isInjured ?? data.is_injured ?? data.injured ?? false,
    championTypes: Array.isArray(data.championTypes) ? data.championTypes.filter(Boolean) : [],
  };

  if (!includePremiumData) return baseWrestler;

  return {
    ...baseWrestler,
    premiumComment: premiumComment || '',
    position: position ?? 0,
  };
};

const getShowBlockWindow = (showStart) => {
  if (!(showStart instanceof Date) || Number.isNaN(showStart.getTime())) {
    return null;
  }

  const showEnd = new Date(showStart);
  showEnd.setDate(showEnd.getDate() + 1);
  showEnd.setHours(12, 0, 0, 0);

  return { showStart, showEnd };
};

export default function FactionLineupPage() {
  const { id: factionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [faction, setFaction] = useState(null);
  const [roster, setRoster] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');
  const [swapsData, setSwapsData] = useState({ enabled: true, blockedAt: null });
  const [autoBlocked, setAutoBlocked] = useState(false);
  const [currentShow, setCurrentShow] = useState(null);
  const [premiumInsights, setPremiumInsights] = useState({});
  const [premiumInsightsLoading, setPremiumInsightsLoading] = useState(false);
  const { isPremium, premiumLoading } = usePremium();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loadLineupData = useCallback(async () => {
    if (!factionId || !currentUser || premiumLoading) return;

    setLoading(true);
    setError('');

    try {
      const factionSnap = await getDoc(doc(db, 'factions', factionId));

      if (!factionSnap.exists()) {
        setError('Fazione non trovata');
        setFaction(null);
        return;
      }

      const factionData = { id: factionSnap.id, ...factionSnap.data() };

      if (factionData.ownerUid !== currentUser.uid) {
        setError('Non sei autorizzato a modificare la line-up di questa fazione');
        return;
      }

      setFaction(factionData);

      let rosterData = null;

      if (factionData.rosterId) {
        const rosterSnap = await getDoc(doc(db, 'rosters', factionData.rosterId));
        if (rosterSnap.exists()) {
          rosterData = { id: rosterSnap.id, ...rosterSnap.data() };
        }
      } else if (factionData.roster) {
        const rosterSnap = await getDocs(
          query(collection(db, 'rosters'), where('name', '==', factionData.roster))
        );

        if (!rosterSnap.empty) {
          rosterData = { id: rosterSnap.docs[0].id, ...rosterSnap.docs[0].data() };
        }
      }

      setRoster(rosterData);

      const wrestlersSnap = await getDocs(collection(db, 'wrestlers'));
      const wrestlersMap = wrestlersSnap.docs
        .map(snap => normalizeWrestler(snap, { includePremiumData: isPremium }))
        .reduce((acc, wrestler) => {
          acc[wrestler.id] = wrestler;
          return acc;
        }, {});

      const nextMembers = mergeFactionWrestlersWithDetails(factionData, wrestlersMap);

      setMembers(nextMembers);
    } catch (loadError) {
      console.error(loadError);
      setError('Errore durante il caricamento della line-up');
    } finally {
      setLoading(false);
    }
  }, [currentUser, factionId, isPremium, premiumLoading]);

  useEffect(() => {
    if (!authLoading && currentUser && !premiumLoading) {
      loadLineupData();
    }

    if (!authLoading && !currentUser) {
      setLoading(false);
    }
  }, [authLoading, currentUser, loadLineupData, premiumLoading]);

  useEffect(() => {
    const fetchSwapsState = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'swaps'));

        if (!snap.exists()) {
          setSwapsData({ enabled: true, blockedAt: null });
          return;
        }

        const data = snap.data();
        setSwapsData({
          enabled: data.enabled !== false,
          blockedAt: data.blockedAt ? new Date(data.blockedAt.seconds * 1000) : null,
        });
      } catch (loadError) {
        console.error(loadError);
        setSwapsData({ enabled: true, blockedAt: null });
      }
    };

    fetchSwapsState();
  }, []);

  useEffect(() => {
    const checkForActiveShow = async () => {
      try {
        const now = new Date();
        const lookbackDate = new Date(now.getTime() - 48 * 60 * 60 * 1000);
        const showsSnap = await getDocs(
          query(collection(db, 'shows'), where('data', '>=', lookbackDate))
        );

        let activeShow = null;

        showsSnap.forEach((docSnap) => {
          const showData = docSnap.data();
          const showStart = showData.data?.toDate();

          if (!showStart) return;

          const showWindow = getShowBlockWindow(showStart);
          if (!showWindow) return;

          if (now >= showWindow.showStart && now <= showWindow.showEnd) {
            activeShow = {
              id: docSnap.id,
              ...showData,
              data: showStart,
              endTime: showWindow.showEnd,
            };
          }
        });

        setCurrentShow(activeShow);
        setAutoBlocked(Boolean(activeShow));
      } catch (loadError) {
        console.error(loadError);
      }
    };

    checkForActiveShow();
    const intervalId = setInterval(checkForActiveShow, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!isPremium || members.length === 0) {
      setPremiumInsights({});
      setPremiumInsightsLoading(false);
      return undefined;
    }

    let ignore = false;
    setPremiumInsightsLoading(true);

    const wrestlersById = members.reduce((acc, member) => {
      if (member?.id) acc[member.id] = member;
      return acc;
    }, {});

    loadPremiumWrestlerInsights({
      db,
      wrestlerIds: members.map(member => member.id),
      wrestlersById,
    })
      .then(nextInsights => {
        if (!ignore) setPremiumInsights(nextInsights);
      })
      .catch(loadError => {
        console.error('Errore caricamento insight premium line-up:', loadError);
        if (!ignore) setPremiumInsights({});
      })
      .finally(() => {
        if (!ignore) setPremiumInsightsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [isPremium, members]);

  if (loading || authLoading) {
    return <LoadingSpinner fullPage />;
  }

  if (!currentUser) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          Devi effettuare l accesso per modificare la line-up.
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate('/auth')}
        >
          Vai al login
        </button>
      </div>
    );
  }

  if (error || !faction) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">{error || 'Fazione non trovata'}</div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate('/profile')}
        >
          Torna al profilo
        </button>
      </div>
    );
  }

  const isSectionBlocked = autoBlocked || !swapsData.enabled;
  const blockedTitle = autoBlocked ? 'Line-up temporaneamente bloccata' : 'Line-up disattivata';
  const blockedDescription = autoBlocked
    ? `C'e uno show in corso. La line-up torna modificabile alle ${currentShow?.endTime?.toLocaleString(
        'it-IT',
        {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }
      )}.`
    : 'Line-up e mercato sono bloccati dalle impostazioni generali degli scambi.';
  const blockedStats = [
    {
      label: 'Fazione',
      value: faction?.name || 'N/D',
      helper: `${members.length} wrestler disponibili`,
    },
    {
      label: 'Sezioni roster',
      value: roster?.hasBackstage ? 'Ring + backstage' : 'Ring',
      helper: roster?.hasMainEventer === false ? 'Senza main eventer' : 'Con main eventer',
    },
  ];


  if (!roster?.hasBackstage && roster?.hasMainEventer === false) {
    return (
      <div className="container py-4 faction-utility-page faction-lineup-page">

        <div className="card brand-card border-0 shadow-sm faction-utility-shell">
          <div className="card-body text-center py-5 faction-utility-empty">
            <i className="bi bi-slash-circle display-4 text-brand-secondary d-block mb-3" />
            <h4 className="brand-title mb-2">Line-Up non disponibile</h4>
            <p className="text-muted-brand mb-0">
              Il roster non usa ne main eventer ne backstage, quindi non c e una
              sezione line-up da gestire.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4 faction-utility-page faction-lineup-page">

      <div className="  brand-card border-0 shadow-sm faction-utility-shell">
        <div className="card-body p-0">
          {isSectionBlocked ? (
            <FactionBlockedState
              title={blockedTitle}
              description={blockedDescription}
              autoBlocked={autoBlocked}
              currentShow={currentShow}
              blockedAt={swapsData.blockedAt}
              stats={blockedStats}
            />
          ) : (
            <EditFactionFormLineUp
              faction={faction}
              members={members}
              roster={roster}
              isPremium={isPremium}
              premiumInsights={premiumInsights}
              premiumInsightsLoading={premiumInsightsLoading}
              onSaved={(updatedFaction) =>
                setFaction((prev) => ({ ...prev, ...updatedFaction }))
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
